/**
 * The stages of a deploy, as a line you can read at a glance.
 *
 * "Building" on its own tells you nothing about how far along a deploy is or
 * what happens next. This shows the whole path — clone, build, push, roll out —
 * with the current step ticking, so a developer watching a slow build knows
 * whether to wait or to go read the log.
 */

import { useEffect, useState } from 'react'
import type { Deployment, DeploymentStatus } from '../lib/types'
import { IconCheck } from './icons'
import { Tooltip } from './overlay'
import type { DeployActivity } from './LogConsole'

interface Stage {
  key: DeploymentStatus
  label: string
  /** What is actually happening, for the step that is currently running. */
  detail: string
}

const STAGES: Stage[] = [
  { key: 'queued', label: 'Queued', detail: 'Waiting for a builder' },
  { key: 'cloning', label: 'Clone', detail: 'Fetching your commit' },
  { key: 'building', label: 'Build', detail: 'Detecting the runtime and building an image' },
  { key: 'pushing', label: 'Push', detail: 'Storing the image in Addis Ababa' },
  { key: 'deploying', label: 'Release', detail: 'Rolling out and waiting for health checks' },
  { key: 'live', label: 'Live', detail: 'Serving traffic' },
]

const ORDER = STAGES.map((stage) => stage.key)

/** Statuses that end a deploy without reaching the end of the line. */
const STOPPED: Partial<Record<DeploymentStatus, string>> = {
  failed: 'Failed',
  canceled: 'Canceled',
  superseded: 'Replaced by a newer deploy',
}

function elapsedSince(iso: string | null, now: number): number | null {
  if (!iso) return null
  const started = Date.parse(iso)
  return Number.isNaN(started) ? null : Math.max(0, (now - started) / 1000)
}

function clock(seconds: number): string {
  const whole = Math.floor(seconds)
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`
}

export default function DeployTimeline({ deployment, activity }: {
  deployment: Deployment
  activity?: DeployActivity | null
}) {
  const stopped = STOPPED[deployment.status]
  // A terminal failure does not tell us which earlier stages completed.
  // Show its reason without falsely labeling the queue as the failed stage.
  const currentIndex = ORDER.indexOf(deployment.status)

  const running = !stopped && deployment.status !== 'live'

  // A live counter only exists while something is actually in flight; once a
  // deploy settles the recorded duration is the honest number.
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!running) return
    const timer = window.setInterval(() => setNow(Date.now()), 1_000)
    return () => window.clearInterval(timer)
  }, [running])

  const anchor = deployment.queued_at ?? deployment.started_at ?? deployment.created_at
  const elapsed = running ? elapsedSince(anchor, now) : deployment.duration_seconds

  const currentActivity = activity?.deploymentId === deployment.id ? activity : null
  const text = currentActivity?.text ?? ''
  let title = ({ queued: 'Your deploy is queued', cloning: 'Fetching your code',
    building: 'Building your application', pushing: 'Publishing your image',
    deploying: 'Preparing your release', live: 'Your app is live',
  } as Partial<Record<DeploymentStatus, string>>)[deployment.status] ?? 'Deployment'
  let detail = STAGES[currentIndex]?.detail ?? 'Waiting for a status update'
  if (deployment.status === 'deploying') {
    if (/^Waiting for .*finish starting|^Retrying linked database|^[^:]+: Database /.test(text)) {
      title = 'Starting your database'
      detail = 'Waiting for the database to accept connections before releasing your app.'
    } else if (text.startsWith('Running pre-deploy command')) {
      title = 'Running pre-deploy command'
      detail = 'Applying your configured migrations or release tasks.'
    } else if (text.startsWith('Database ready.') || text.startsWith('Pre-deploy command succeeded')) {
      title = 'Launching your app'
      detail = 'Starting the new version and checking its health.'
    }
  }
  const quiet = running && currentActivity && now - Date.parse(currentActivity.ts) > 30_000

  return (
    <div className={`timeline ${running ? 'is-running' : ''} ${stopped ? 'is-stopped' : ''}`} aria-label="Deploy progress">
      <div className="deploy-mission">
        <div className="deploy-orbit" aria-hidden="true">
          <span /><span />
          <b>{deployment.status === 'live' ? <IconCheck size={22} /> : stopped ? '!' : '↑'}</b>
        </div>
        <div className="deploy-mission-copy" role="status" aria-live="polite">
          <span className="deploy-eyebrow">{running ? 'Deployment in progress' : deployment.status === 'live' ? 'Deployment complete' : stopped}</span>
          <h3>{stopped ?? (deployment.status === 'live' ? 'Your app is live' : title)}</h3>
          <p>{stopped ? 'Deployment stopped. Review the reason and logs below.' : detail}</p>
        </div>
        {elapsed !== null && elapsed !== undefined && (
          <div className="deploy-clock"><span className="tabular">{clock(elapsed)}</span><small>elapsed</small></div>
        )}
      </div>
      <div className="deploy-flow" aria-hidden="true"><span /></div>
      <ol className="timeline-steps">
        {STAGES.map((stage, index) => {
          const reached = currentIndex >= 0 && index < currentIndex
          const active = index === currentIndex && running
          const failedHere = Boolean(stopped) && index === currentIndex
          const state = failedHere
            ? 'failed'
            : reached || deployment.status === 'live'
              ? 'done'
              : active
                ? 'active'
                : 'pending'

          return (
            <li key={stage.key} className={`timeline-step ${state}`} aria-current={active ? 'step' : undefined}>
              {/* Every step says what it does, not only the one running: the
                  question "what is it doing now" is usually followed by "and
                  what happens after that". */}
              <Tooltip label={stage.detail}>
                <span className="timeline-dot" aria-hidden="true">
                  {state === 'done' && <IconCheck className="" size={11} />}
                  {state === 'failed' && '✕'}
                </span>
              </Tooltip>
              <span className="timeline-label">{stage.label}</span>
            </li>
          )
        })}
      </ol>

      {stopped && <p className="timeline-caption tiny">
          <span style={{ color: 'var(--danger)' }}>
            {deployment.error_message ?? stopped}
          </span>
      </p>}
      {running && currentActivity && (
        <div className="deploy-activity">
          <span className="deploy-eyebrow">Latest activity</span>
          <p>{text.slice(0, 280)}</p>
          {quiet && <small className="muted">Waiting for the next update. You can inspect the live logs below.</small>}
        </div>
      )}
    </div>
  )
}
